import { ENV_CONFIG } from '@/shared/config'
import { ROUTER } from '@/shared/config'
import { useIsLogin } from '@/shared/context'
import {
  breadcrumbMap,
  buildEffectiveMenu,
  flattenLeaves,
  getMenuIcon,
  injectDataSyncMenu,
  menuTrail,
} from '@/shared/lib/menu'
import type { MenuItemNode } from '@/shared/lib/menu'
import { STORAGE, getAuthToken, setLocalStorage } from '@/shared/lib/storage'
import { UiLayout } from '@/shared/ui'
import type React from 'react'
import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import LayoutAdmin from './LayoutAdmin'

const layoutStyle: React.CSSProperties = {
  overflow: 'hidden',
  display: 'block',
}

interface MainLayoutProps {
  type?: string
  children?: React.ReactNode
}

// Màn không nằm trong cây menu (vào từ menu người dùng) nên menuTrail rỗng — khai trail riêng.
const EXTRA_TRAILS: Record<string, { label: string; path?: string }[]> = {
  [ROUTER.CHU_KY_CUA_TOI]: [
    { label: 'Trang chủ', path: ROUTER.DEFAULT },
    { label: 'Chữ ký của tôi' },
  ],
}

const toAntdItem = (
  m: MenuItemNode
): {
  key: string
  label: React.ReactNode
  icon?: React.ReactNode
  children?: unknown[]
} => ({
  key: m.key,
  label: m.externalUrl ? (
    // Trang Razor của BE: tải trang thật, không đi qua router của SPA.
    <a
      href={m.externalUrl}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: 'inherit', textDecoration: 'none' }}
    >
      {m.label}
    </a>
  ) : m.path ? (
    <Link to={m.path} style={{ color: 'inherit', textDecoration: 'none' }}>
      {m.label}
    </Link>
  ) : (
    m.label
  ),
  icon: getMenuIcon(m.icon),
  ...(m.children?.length ? { children: m.children.map(toAntdItem) } : {}),
})

// ponytail: type ('isAdmin'/'isUser') không còn phân nhánh menu — menu giờ lấy thẳng từ
// GetListTab (đồng bộ với quyền BE thực tế). Giữ prop để không phải sửa AdminRoutes/PrivateRoutes.
const MainLayout = ({ children }: MainLayoutProps) => {
  const navigate = useNavigate()
  const location = useLocation()
  const isLoginLocalStorage = getAuthToken()
  const { setIsLogin } = useIsLogin()

  const { listMenu, listTabs } = useSelector((state: any) => state?.appGlobal)

  const menuItems = useMemo<MenuItemNode[]>(() => {
    const effective = injectDataSyncMenu(
      buildEffectiveMenu(listMenu),
      listTabs,
      ENV_CONFIG.VITE_BACKEND_BASE_URL
    )

    // Sao chép an toàn để không mutate trực tiếp Redux store
    return (effective ?? []).map((item) => ({
      ...item,
      children: item.children ? [...item.children] : undefined,
    }))
  }, [listMenu, listTabs])

  const menu = useMemo(() => menuItems.map(toAntdItem), [menuItems])
  // Mục ngoài SPA (externalUrl) tự điều hướng bằng thẻ <a>, không được vào map của router.
  const routedLeaves = useMemo(
    () => flattenLeaves(menuItems).filter((m) => !m.externalUrl),
    [menuItems]
  )
  const pathByKey = useMemo(() => new Map(routedLeaves.map((m) => [m.key, m.path])), [routedLeaves])
  const keyByPath = useMemo(() => new Map(routedLeaves.map((m) => [m.path, m.key])), [routedLeaves])

  // Breadcrumb lấy từ cây menu; các màn chi tiết ngoài menu tự động ghép theo tiền tố cha hoặc fallback từ breadcrumbMap.
  const breadcrumbItems = useMemo(() => {
    // 1. Khớp chính xác từ menuTrail
    const trail = menuTrail(menuItems, location.pathname).map((m) => ({
      label: m.label,
      path: m.path,
    }))
    if (trail.length) return trail

    // 2. Tra bảng EXTRA_TRAILS
    if (EXTRA_TRAILS[location.pathname]) return EXTRA_TRAILS[location.pathname]

    // 3. Khớp tiền tố menu cho các màn chi tiết (ví dụ: /ho-so/:id, /ho-so/:id/phat-hanh)
    const leaves = flattenLeaves(menuItems)
    for (const leaf of leaves) {
      if (leaf.path && leaf.path !== '/' && location.pathname.startsWith(`${leaf.path}/`)) {
        const parentTrail = menuTrail(menuItems, leaf.path).map((m) => ({
          label: m.label,
          path: m.path,
        }))
        const subPath = location.pathname.slice(leaf.path.length + 1)
        if (subPath.includes('phat-hanh')) {
          return [
            ...parentTrail,
            { label: 'Chi tiết hồ sơ', path: location.pathname.replace(/\/phat-hanh.*$/, '') },
            { label: 'Phát hành hồ sơ' },
          ]
        }
        return [...parentTrail, { label: 'Chi tiết hồ sơ' }]
      }
    }

    // 4. Suy luận fallback theo breadcrumbMap
    const segments = location.pathname.split('/').filter(Boolean)
    if (segments.length > 0) {
      let cur = ''
      const items: { label: string; path?: string }[] = [
        { label: 'Trang chủ', path: ROUTER.DEFAULT },
      ]
      for (let i = 0; i < segments.length; i++) {
        const seg = segments[i]
        cur += `/${seg}`
        const label =
          breadcrumbMap[seg] ||
          (i === segments.length - 1 && segments[i - 1] === 'ho-so' ? 'Chi tiết' : seg)
        const isLast = i === segments.length - 1
        items.push({ label, path: isLast ? undefined : cur })
      }
      return items
    }

    return []
  }, [menuItems, location.pathname])

  const currentKey = keyByPath.get(location.pathname)
  const selectedKey = [(location.state as any)?.activeMenu || currentKey || '']

  const onClickMenu = (menuInfo: any) => {
    setLocalStorage(STORAGE.KEY_MENU_ACTIVE, menuInfo.keyPath)
    const path = pathByKey.get(menuInfo.key)
    if (path) navigate(path)
  }

  // setIsLogin la setter cua context, giu nguyen giua cac lan render.
  // biome-ignore lint/correctness/useExhaustiveDependencies: them setter vao deps chi lam nhieu
  useEffect(() => {
    if (isLoginLocalStorage) {
      setIsLogin(isLoginLocalStorage)
    }
  }, [isLoginLocalStorage])
  return (
    <UiLayout style={layoutStyle}>
      {menu?.length ? (
        <LayoutAdmin
          menuAdmin={menu}
          selectedKey={selectedKey}
          onClickMenu={onClickMenu}
          breadcrumbItems={breadcrumbItems}
        >
          {children}
        </LayoutAdmin>
      ) : (
        children
      )}
    </UiLayout>
  )
}

export default MainLayout
